import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  GestorDashboardBreakdownList,
  GestorDashboardMetricCard,
  type GestorDashboardBreakdownListRow,
} from '@/components/Gestores/GestorDashboardWidgets'
import { GestorDashboardDailyBars } from '@/components/Gestores/GestorDashboardDailyBars'
import { GestorDashboardExecutorPanel } from '@/components/Gestores/GestorDashboardExecutorPanel'
import { buildGestorDashboardTaskPath } from '@/components/Gestores/gestorDashboardNavigation'
import { formatEffortMinutes } from '@/components/Gestores/gestorDashboardFormat'
import type {
  GestorDashboardCollaboratorView,
  GestorDashboardSummary,
  GestorDashboardTimeEntry,
} from '@/components/Gestores/gestorDashboardTypes'

type Props = {
  collaborator: GestorDashboardCollaboratorView
  summary: GestorDashboardSummary
  entries: GestorDashboardTimeEntry[]
  selectedExecutor: string
  period: { createdFrom: string; createdTo: string }
  squadId?: number | null
  loading?: boolean
  onExecutorSelect: (executor: string) => void
  onExecutorClear: () => void
}

export function GestorDashboardCollaboratorView({
  collaborator,
  summary,
  entries,
  selectedExecutor,
  period,
  squadId = null,
  loading = false,
  onExecutorSelect,
  onExecutorClear,
}: Props) {
  const navigate = useNavigate()
  const executorPanelRef = useRef<HTMLElement>(null)

  const openTask = (taskId: number) => {
    navigate(buildGestorDashboardTaskPath(taskId, { ...period, squadId }))
  }

  const handleExecutorClick = (row: GestorDashboardBreakdownListRow) => {
    onExecutorSelect(row.label)
  }

  const activeExecutorKey = useMemo(() => {
    if (!selectedExecutor) return null
    const match =
      collaborator.byExecutor.find((row) => row.label === selectedExecutor) ??
      collaborator.tasksByExecutor.find((row) => row.label === selectedExecutor)
    return match?.key ?? null
  }, [collaborator.byExecutor, collaborator.tasksByExecutor, selectedExecutor])

  useEffect(() => {
    if (!selectedExecutor) return
    executorPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selectedExecutor])

  const totalTasks = useMemo(
    () => collaborator.tasksByExecutor.reduce((sum, row) => sum + row.count, 0),
    [collaborator.tasksByExecutor],
  )

  const avgDailyMinutes = useMemo(
    () =>
      collaborator.dailyHours.length > 0
        ? Math.round(summary.totalEffortMinutes / collaborator.dailyHours.length)
        : 0,
    [collaborator.dailyHours.length, summary.totalEffortMinutes],
  )

  const topTasksRows = useMemo(
    () =>
      collaborator.topTasksByEffort.map((row) => ({
        key: row.key,
        label: row.executor ? `${row.task} · ${row.executor}` : row.task,
        taskId: row.taskId,
        effortMinutes: row.effortMinutes,
      })),
    [collaborator.topTasksByEffort],
  )

  return (
    <div className='space-y-4'>
      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <GestorDashboardMetricCard label='Colaboradores' value={String(summary.executorCount)} />
        <GestorDashboardMetricCard label='Horas apontadas' value={formatEffortMinutes(summary.totalEffortMinutes)} />
        <GestorDashboardMetricCard label='Tarefas no período' value={String(totalTasks)} />
        <GestorDashboardMetricCard label='Média horas/dia' value={formatEffortMinutes(avgDailyMinutes)} />
      </div>

      <div className='grid gap-3 xl:grid-cols-2'>
        <GestorDashboardBreakdownList
          title='Esforço por executor'
          rows={collaborator.byExecutor}
          onRowClick={handleExecutorClick}
          activeRowKey={activeExecutorKey}
        />
        <GestorDashboardBreakdownList
          title='Tarefas por executor'
          rows={collaborator.tasksByExecutor}
          valueKey='count'
          valueFormatter={(value) => String(value)}
          onRowClick={handleExecutorClick}
          activeRowKey={activeExecutorKey}
        />
      </div>

      {selectedExecutor ? (
        <GestorDashboardExecutorPanel
          ref={executorPanelRef}
          executor={selectedExecutor}
          entries={entries}
          loading={loading}
          onClear={onExecutorClear}
          onTaskOpen={openTask}
        />
      ) : (
        <>
          <GestorDashboardBreakdownList title='Tarefas de maior esforço' rows={topTasksRows} onTaskOpen={openTask} />
          <GestorDashboardDailyBars title='Registro diário de horas' rows={collaborator.dailyHours} />
          <p className='text-xs text-text-muted'>
            Clique em um colaborador acima para ver as tarefas segmentadas por pessoa.
          </p>
        </>
      )}
    </div>
  )
}
